import type { MeResponse } from '@complan/contracts';
import { useState } from 'react';
import { LogOut, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { hasCapability, signOut } from '@/app/session/session';
import { DevAccountSwitcher } from '@/app/session/DevAccountSwitcher';

/**
 * The sticky white top bar (UI spec §2.2).
 *
 * Left: the page title and a subtitle carrying planning-year / refresh context.
 * Right: the primary `+ New initiative` action, then the user avatar. The
 * prototype role selector of §2.2 is intentionally omitted (§0.1) — there is no
 * user-facing role switcher; role is derived from the session. The `+ New
 * initiative` action is gated on `initiative:write` so it is hidden for read-only
 * principals, mirroring the server capability check.
 *
 * {@link DevAccountSwitcher} sits before the primary action in development builds
 * and is **not** that role selector: it does not alter the client's view of its
 * own roles, it starts a real sign-in as a different dev account and lets the
 * server resolve that account's roles as usual. It renders `null` in any
 * production build - see its own module doc for why it cannot ship.
 */
export interface TopBarProps {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly me: MeResponse | undefined;
}

export function TopBar({ title, subtitle, me }: TopBarProps): React.JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canCreate = hasCapability(me, 'initiative:write');

  return (
    <header className="bg-panel border-line sticky top-0 z-30 flex items-center justify-between border-b px-[18px] py-2.5">
      <div>
        <h1 className="text-[16px] font-semibold leading-tight text-txt">{title}</h1>
        {subtitle ? <p className="text-[11.5px] text-muted-foreground">{subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-3">
        <DevAccountSwitcher currentSubject={me?.subject} />
        {canCreate ? (
          <Button onClick={() => navigate('/intake')} className="gap-1.5">
            <Plus aria-hidden className="size-4" />
            {t('topBar.newInitiative')}
          </Button>
        ) : null}
        <Avatar subject={me?.subject} />
        {me !== undefined ? <SignOutButton /> : null}
      </div>
    </header>
  );
}

/**
 * Ends the session (R1.6) - see {@link signOut}. On success the page navigates
 * away, so the busy state is only ever cleared by a failure.
 */
function SignOutButton(): React.JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const onClick = async (): Promise<void> => {
    setBusy(true);
    setFailed(false);
    try {
      await signOut();
    } catch {
      setBusy(false);
      setFailed(true);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => void onClick()}
      disabled={busy}
      aria-busy={busy}
      title={failed ? t('topBar.signOutFailed') : undefined}
      className="gap-1.5"
    >
      <LogOut aria-hidden className="size-4" />
      {busy ? t('topBar.signingOut') : t('topBar.signOut')}
      {failed ? (
        <span role="alert" className="sr-only">
          {t('topBar.signOutFailed')}
        </span>
      ) : null}
    </Button>
  );
}

function Avatar({ subject }: { subject: string | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  const initials = toInitials(subject);
  const account = t('common.account');
  return (
    <span
      aria-label={subject ? t('common.signedInAs', { subject }) : account}
      title={subject ?? account}
      className="bg-navy-2 flex size-7 items-center justify-center rounded-full text-[11px] font-semibold text-white"
    >
      {initials}
    </span>
  );
}

function toInitials(subject: string | undefined): string {
  if (!subject) return '·';
  const cleaned = subject.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
  if (cleaned.length === 0) return '·';
  const parts = cleaned.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${second}`.toUpperCase() || '·';
}
